import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  AppBar,
  Toolbar,
  Avatar,
  Menu,
  MenuItem as MenuItemComponent,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Button
} from '@mui/material';
import {
  NotificationsOutlined,
  FolderOutlined
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/logo.png';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const StaffProjectsPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeTab, setActiveTab] = useState(1); // Projects tab
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const userInfo = localStorage.getItem('userInfo');
    
    if (!token) {
      navigate('/login');
      return;
    }
    
    if (userInfo) {
      const userData = JSON.parse(userInfo);
      if (!userData.is_staff) {
        navigate('/dashboard');
        return;
      }
      setUser(userData);
      loadProjects(userData.username);
    }
  }, [navigate]);

  const loadProjects = async (staffName) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await axios.get(
        `${API_URL}/projects/admin/all/?consultant=${encodeURIComponent(staffName)}`,
        { headers: { Authorization: `Token ${token}` } }
      );
      
      const projectsList = response.data.results || response.data || [];
      console.log('Loaded projects:', projectsList);
      setProjects(projectsList);
    } catch (error) {
      console.error('Error loading projects:', error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 0) navigate('/staff');
    if (newValue === 2) navigate('/staff/messages');
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userInfo');
    navigate('/login');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'dpr_created': return '#2196f3';
      case 'under_review': return '#ff9800';
      case 'approved': return '#4caf50';
      case 'commissioned': return '#4caf50';
      case 'submitted': return '#666';
      default: return '#666';
    }
  };

  const getStatusBgColor = (status) => {
    switch (status) {
      case 'dpr_created': return '#e3f2fd';
      case 'under_review': return '#fff3e0';
      case 'approved': return '#e8f5e8';
      case 'commissioned': return '#e8f5e8';
      case 'submitted': return '#f5f5f5';
      default: return '#f5f5f5';
    }
  };

  const capitalizeWords = (str) => {
    if (!str) return 'N/A';
    return str
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#f8f9fa', minHeight: '100vh', pb: 8 }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ bgcolor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <Toolbar sx={{ px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 4 }}>
            <img src={logo} alt="TICSPL" style={{ height: '32px', marginRight: '12px' }} />
            <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 600 }}>
              TICSPL Staff Portal
            </Typography>
          </Box>
          
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            sx={{ 
              flexGrow: 1,
              '& .MuiTab-root': {
                color: '#666',
                fontWeight: 500,
                textTransform: 'none',
                fontSize: '16px'
              },
              '& .Mui-selected': {
                color: '#1976d2 !important'
              }
            }}
          >
            <Tab label="Dashboard" />
            <Tab label="Projects" />
            <Tab label="Messages" />
          </Tabs>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton sx={{ color: '#666' }}>
              <NotificationsOutlined />
            </IconButton>
            <Button
              color="inherit"
              onClick={handleMenuOpen}
              sx={{ 
                color: '#333',
                textTransform: 'none',
                fontWeight: 500
              }}
              endIcon={<Avatar sx={{ width: 32, height: 32, bgcolor: '#ff9800' }}>{user?.username?.charAt(0).toUpperCase()}</Avatar>}
            >
              {user?.username}
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItemComponent onClick={handleMenuClose}>Profile</MenuItemComponent>
              <MenuItemComponent onClick={handleLogout}>Logout</MenuItemComponent>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#333' }}>
          My Assigned Projects
        </Typography>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography color="text.secondary">Loading projects...</Typography>
          </Box>
        ) : projects.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <FolderOutlined sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No projects assigned yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Contact your administrator for project assignments
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {projects.map((project) => (
              <Grid item xs={12} sm={6} md={4} key={project.id}>
                <Card sx={{ 
                  height: '100%',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                  }
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                        {capitalizeWords(project.name)}
                      </Typography>
                      <Chip 
                        label={capitalizeWords(project.status_display || project.status)}
                        size="small"
                        sx={{ 
                          bgcolor: getStatusBgColor(project.status),
                          color: getStatusColor(project.status),
                          fontWeight: 600
                        }}
                      />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Entrepreneur
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {capitalizeWords(project.entrepreneur_name)}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Project Type
                      </Typography>
                      <Typography variant="body1">
                        {capitalizeWords(project.project_type)}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Investment
                      </Typography>
                      <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 600 }}>
                        {project.formatted_investment}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Industry
                      </Typography>
                      <Chip 
                        label={capitalizeWords(project.industry_type)}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default StaffProjectsPage;
